import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { DOCS_URL, TESTNET_URL } from '@/utils/externalLinks';

export const MOBILE_BREAKPOINT = 'md';
export const DESKTOP_BREAKPOINT = 'lg';

export interface NavItem {
  label: MessageDescriptor;
  value: number;
  path: string;
  enabled: boolean;
  /** Tucked into the desktop "Tools" dropdown instead of the top-level tabs. */
  overflow: boolean;
  /** Lives under the desktop Rewards hover menu (still inline in the mobile drawer). */
  rewardsMenu: boolean;
  /** `path` is an absolute URL — open in a new tab, not via the router. */
  external?: boolean;
}

// Array order is the display order. `overflow: true` → Tools dropdown; `rewardsMenu: true` →
// Rewards hover menu. Mobile drawer renders every enabled item inline regardless.
// `value` is a stable id (feature gates in useFilteredNavItems key off it), not a position —
// don't renumber when reordering.
// Labels are Lingui descriptors; useFilteredNavItems resolves them to display strings.
export const NAV_ITEMS: readonly NavItem[] = [
  {
    label: msg({ comment: 'Header nav item', message: 'Trade' }),
    value: 0,
    path: '/trade',
    enabled: true,
    overflow: false,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Portfolio' }),
    value: 1,
    path: '/portfolio',
    enabled: true,
    overflow: false,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Tournaments' }),
    value: 9,
    path: '/tournaments',
    enabled: true,
    overflow: false,
    rewardsMenu: false,
  },
  {
    label: msg({
      comment:
        'Header nav item. A DeFi product category (a managed strategy users deposit into), not a strongroom or storage locker — most languages either keep "Vaults" or use their own borrowed form. Do not reach for the word meaning "storage room".',
      message: 'Vaults',
    }),
    value: 3,
    path: '/vaults',
    enabled: false,
    overflow: false,
    rewardsMenu: false,
  },
  {
    label: msg({
      comment:
        'Header nav item. "The Bag" is the product name of a rewards feature — keep it in Latin script and do not translate or transliterate it.',
      message: 'The Bag',
    }),
    value: 4,
    path: '/bag',
    enabled: false,
    overflow: false,
    rewardsMenu: false,
  },
  // Rewards hover menu
  {
    label: msg({ comment: 'Header nav item', message: 'mPoints' }),
    value: 8,
    path: '/mpoints',
    enabled: true,
    overflow: false,
    rewardsMenu: true,
  },
  {
    label: msg({
      comment:
        'Header nav item. "Points" is the name of a reward currency, like the neighbouring "mPoints" — keep it in Latin script and do not translate or transliterate it. The same applies wherever the word names the currency ("Points History", "Weekly Points", …).',
      message: 'Points',
    }),
    value: 5,
    path: '/points',
    enabled: true,
    overflow: false,
    rewardsMenu: true,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Referrals' }),
    value: 6,
    path: '/referrals',
    enabled: true,
    overflow: false,
    rewardsMenu: true,
  },
  // Tools dropdown
  {
    label: msg({ comment: 'Header nav item', message: 'Analytics' }),
    value: 16,
    path: '/dashboard',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Leaderboard' }),
    value: 2,
    path: '/leaderboard',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Funding Rates' }),
    value: 15,
    path: '/funding-rates',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'Builder Codes' }),
    value: 12,
    path: '/builders',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({ comment: 'Header nav item', message: 'API Keys' }),
    value: 10,
    path: '/apikeys',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({
      comment: 'Header nav item linking to the release notes page. Sits in the Tools menu beside "API Keys" and "Docs".',
      message: 'Changelog',
    }),
    value: 14,
    path: '/changelog',
    enabled: true,
    overflow: true,
    rewardsMenu: false,
  },
  {
    label: msg({
      comment: 'Header nav item linking to the documentation site. Sits in the Tools menu beside "API Keys" and "Testnet".',
      message: 'Docs',
    }),
    value: 13,
    path: DOCS_URL,
    enabled: true,
    overflow: true,
    external: true,
    rewardsMenu: false,
  },
  {
    label: msg({
      comment:
        'Header nav item linking to the test deployment. Keep "Testnet" in Latin script — it is the network\'s name, and it sits in the same menu as other untranslated names.',
      message: 'Testnet',
    }),
    value: 11,
    path: TESTNET_URL,
    enabled: true,
    overflow: true,
    external: true,
    rewardsMenu: false,
  },
];

// Partners see /referrals relabelled — that route renders the partner dashboard instead.
export const REFERRALS_NAV_VALUE = 6;
export const PARTNER_NAV_LABEL = msg({
  comment: 'Header nav item replacing Referrals for partners',
  message: 'VIP Rewards',
});

export const DELEGATED_CHIP_LABEL = msg({ comment: 'Header chip: delegated account session', message: 'Delegated' });
export const OWNER_CHIP_LABEL = msg({ comment: 'Header chip: delegated account owner', message: 'Owner' });

export const ACTIVE_PROPS = {
  className: 'active',
};

/** Highlights nav items for nested routes (e.g. /tournaments/:slug). */
export function isNavPathActive(navPath: string, pathname: string): boolean {
  if (pathname === navPath) return true;
  if (navPath.startsWith('/tournaments') && (pathname === '/tournaments' || pathname.startsWith('/tournaments/'))) {
    return true;
  }
  return false;
}
